// services/chatHistoryService.ts - Servicio para manejo de historial de chat

const API_BASE_URL = '/api';

export interface ChatSession {
  id: string;
  title: string;
  user_id: number;
  message_count: number;
  created_at: string;
  updated_at: string;
  last_activity: string;
  is_active: boolean;
}

export interface ChatGroup {
  label: string;
  chats: ChatSession[];
}

export interface ChatMessage {
  id: number;
  session_id: string;
  user_id: number;
  message_text: string;
  response_text: string;
  message_type: 'user' | 'ai';
  created_at: string;
  confidence_score?: number;
}

class ChatHistoryService {
  // ✅ MÉTODO AUXILIAR: Obtener token
  private getToken(): string | null {
    return localStorage.getItem('token') || localStorage.getItem('kikai_token');
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}) {
    try {
      const url = `${API_BASE_URL}${endpoint}`;
      console.log(`🔗 Chat API Request: ${url}`);
      
      // ✅ OBTENER TOKEN
      const token = this.getToken();
      
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          ...options.headers,
        },
        credentials: 'same-origin',
        ...options,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(`✅ Chat API Success: ${url}`, data);
      return data;
    } catch (error) {
      console.error(`❌ Chat API Error (${endpoint}):`, error);
      throw error;
    }
  }

  async createChatSession(userId: string, title: string = 'Nueva conversación'): Promise<ChatSession> {
    try {
      const response = await this.makeRequest('/chats/session', {
        method: 'POST',
        body: JSON.stringify({ userId: parseInt(userId), title }),
      });

      return response.session;
    } catch (error) {
      console.error('Error creando sesión de chat:', error);
      throw error;
    }
  }

  async getUserChatsGrouped(userId: string): Promise<ChatGroup[]> {
  try {
    const response = await this.makeRequest(`/chats/user/${userId}/grouped`);
    
    // El backend ya devuelve groups como array, usarlo directamente
    const groups: ChatGroup[] = Array.isArray(response.groups) ? response.groups : [];

    return groups;
  } catch (error) {
    console.error('Error obteniendo chats agrupados:', error);
    // Devolver estructura vacía en caso de error
    return [{
      label: 'Hoy',
      chats: []
    }];
  }
}

/**
 * 🔧 MÉTODO DE COMPATIBILIDAD: addMessage
 * Agregado para compatibilidad con TechnicalChat.tsx
 */
  async addMessage(chatId: string, messageData: { 
  text: string; 
  sender: 'user' | 'ai'; 
  metadata?: any 
}): Promise<any> {
  console.log('⚠️ [COMPATIBILITY] Usando addMessage - migrando a saveMessage automáticamente');
  
  // ✅ REDIRIGIR A saveMessage CON LOS PARÁMETROS CORRECTOS
  return await this.saveMessage(
    chatId, 
    messageData.text, 
    messageData.sender, 
    messageData.metadata
  );
}

  async getChatMessagesdemo(sessionId: string): Promise<ChatMessage[]> {
    try {
      const response = await this.makeRequest(`/chats/${sessionId}/messages`);
      
      // ✅ MANEJO ROBUSTO DE RESPUESTAS
      const messages = response.messages || response.data || [];
      
      return Array.isArray(messages) ? messages : [];
      
    } catch (error) {
      console.error(`❌ [GET-MESSAGES] Error obteniendo mensajes ${sessionId}:`, error);
      return []; // Devolver array vacío en lugar de lanzar error
    }
  }

  async searchChats(userId: string, query: string): Promise<ChatSession[]> {
    try {
      // Por ahora, buscar en todas las sesiones del usuario
      const groups = await this.getUserChatsGrouped(userId);
      const allChats = groups.flatMap(group => group.chats);
      
      // Filtrar por título que contenga la query
      return allChats.filter(chat => 
        chat.title.toLowerCase().includes(query.toLowerCase())
      );
    } catch (error) {
      console.error('Error buscando chats:', error);
      return [];
    }
  }

  async saveChatMessage(sessionId: string, userId: string, message: string, response: string, messageType: 'user' | 'ai') {
    try {
      await this.makeRequest(`/chats/${sessionId}/messages`, {
        method: 'POST',
        body: JSON.stringify({
          message,
          response,
          messageType,
          userId: parseInt(userId)
        }),
      });
    } catch (error) {
      console.error('Error guardando mensaje:', error);
      // No lanzar error para no interrumpir el flujo del chat
    }
  }
  
async saveMessage(
  chatId: string,
  content: string | any,
  sender: 'user' | 'ai',
  metadata?: any
): Promise<any> { 
  try {
    // Normaliza el contenido a string
    const safeContent =
      typeof content === 'string'
        ? content
        : typeof content?.text === 'string'
          ? content.text
          : JSON.stringify(content ?? '');

    // No serialices metadata dos veces: manda objeto si existe
    const safeMetadata =
      metadata !== undefined ? metadata
      : (typeof content === 'object' && content?.metadata !== undefined ? content.metadata : undefined);

    console.log('💾 [SAVE-MESSAGE] Guardando:', {
      chatId,
      sender,
      contentLength: safeContent.length,
      hasMetadata: safeMetadata !== undefined
    });
    
    // ✅ EXTRAER deviceInfo de metadata para enviarlo en el body raíz
    const deviceInfo = safeMetadata?.deviceInfo;
    const metadataWithoutDevice = safeMetadata ? { ...safeMetadata } : undefined;
    if (metadataWithoutDevice?.deviceInfo) {
      delete metadataWithoutDevice.deviceInfo;
    }
    const response = await this.makeRequest(`/chats/${chatId}/messages`, {
      method: 'POST',
      body: JSON.stringify({
        content: safeContent,
        sender,
        metadata: safeMetadata,
        deviceInfo  // ✅ ENVIAR deviceInfo en raíz del body
      }),
    });

    console.log('✅ [SAVE-MESSAGE] Mensaje guardado exitosamente');
    return response;

  } catch (error: any) {
    console.error('❌ [SAVE-MESSAGE] Error guardando mensaje:', error);
    throw new Error(`Error guardando mensaje: ${error?.message ?? String(error)}`);
  }
}

 /**
   * 🔧 MÉTODO FALTANTE: getChatSession
   * Este método era llamado por TechnicalChat.tsx pero no existía
   */
async getChatSession(chatId: string): Promise<any> {
  try {
    console.log(`📂 [GET-SESSION] Obteniendo sesión: ${chatId}`);
    
    // Obtener mensajes del backend
    const response = await this.makeRequest(`/chats/${chatId}/messages`);
    const backendMessages = response.messages || [];
    
    console.log('🐛 [DEBUG] Backend messages raw:', backendMessages.slice(0, 1));
    
    if (!Array.isArray(backendMessages)) {
      console.error('❌ [GET-SESSION] messages no es array');
      return { id: chatId, title: 'Conversación', messages: [] };
    }
    
    // ✅ MAPEO CRÍTICO: content → text
      const formattedMessages = backendMessages.map(msg => {
        const mapped = {
          id: (msg.id || Date.now()).toString(),
          text: msg.content || '',
          timestamp: msg.created_at,
          isUser: msg.sender === 'user',
          metadata: this.parseMetadata(msg.metadata)  // ✅ Usar método auxiliar
        };
        
        if (!mapped.text) {
          console.warn('⚠️ [MAP] Mensaje sin texto:', msg.id, 'content:', msg.content);
        }
        
        return mapped;
      });
    
console.log('✅ [GET-SESSION] Mapeados:', formattedMessages.length, 'mensajes');
console.log('🐛 [DEBUG] Primer mensaje mapeado:', formattedMessages[0]);

// ✅ LOG ADICIONAL: Verificar que text existe
console.log('🔍 [CRITICAL] Primer mensaje.text:', formattedMessages[0]?.text);
console.log('🔍 [CRITICAL] Todos los textos:', formattedMessages.map(m => m.text?.substring(0, 20)));

const result = {
  id: chatId,
  title: response.session?.title || `Conversación ${chatId.substring(0, 8)}...`,
  messages: formattedMessages
};

return result;
// ✅ LOG: Verificar objeto completo que se devuelve
console.log('🔍 [CRITICAL] Resultado getChatSession:', JSON.stringify(result, null, 2));

    
  } catch (error) {
    console.error(`❌ [GET-SESSION] Error:`, error);
    return { id: chatId, title: 'Conversación', messages: [] };
  }
}



  
    /**
   * 🗑️ Eliminar una sesión de chat por ID
   * Intenta usar DELETE /chats/:id. Retorna true si la operación fue exitosa.
   * Maneja respuestas 204 (sin contenido) y JSON con { success: boolean }.
   */
  async deleteChatSession(chatId: string): Promise<boolean> {
    try {
      const url = `${API_BASE_URL}/chats/${chatId}`;
      console.log(`🗑️ [DELETE-SESSION] ${url}`);
      
     // ✅ OBTENER TOKEN
      const token = this.getToken();
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'same-origin',
      });

      // 204 No Content => éxito
      if (response.status === 204) {
        console.log('✅ [DELETE-SESSION] 204 No Content');
        return true;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Si devuelve JSON, validar flags comunes
      try {
        const data = await response.json();
        const ok = (data && (data.success === true || data.deleted === true || data.ok === true));
        console.log('✅ [DELETE-SESSION] Respuesta JSON:', data);
        return ok ?? true;
      } catch {
        // Respuesta OK sin JSON
        console.log('ℹ️ [DELETE-SESSION] Respuesta sin JSON; asumiendo éxito');
        return true;
      }

    } catch (error) {
      console.error('❌ [DELETE-SESSION] Error eliminando sesión:', error);
      return false;
    }
  }
  /**
   * 🔧 MÉTODO AUXILIAR: Parsear metadata de forma segura
   */
  private parseMetadata(metadata: any): any {
    if (!metadata) return {};
    
    try {
      if (typeof metadata === 'string') {
        return JSON.parse(metadata);
      }
      return metadata;
    } catch (error) {
      console.warn('⚠️ [METADATA] Error parseando metadata:', error);
      return {};
    }
  }

  /**
   * 🔧 MÉTODO AUXILIAR: Verificar si una sesión existe
   */
  async sessionExists(chatId: string): Promise<boolean> {
    try {
      const response = await this.makeRequest(`/chats/${chatId}`);
      return response.success === true;
    } catch (error) {
      console.warn(`⚠️ [SESSION-EXISTS] Error verificando ${chatId}:`, error);
      return false;
    }
  }
/**
 * 🔧 MÉTODO AUXILIAR: Verificar existencia de chat
 */
async chatExists(chatId: string): Promise<boolean> {
  try {
    const response = await this.makeRequest(`/chats/${chatId}`);
    return response.success === true;
  } catch (error) {
    console.warn('⚠️ [CHAT-EXISTS] Error verificando chat:', error);
    return false;
  }
}

/**
 * 🔧 MÉTODO AUXILIAR: Obtener mensajes de un chat
 */
async getChatMessages(sessionId: string): Promise<ChatMessage[]> {
  try {
    console.log(`📖 [GET-MESSAGES] Obteniendo mensajes: ${sessionId}`);
    
    const response = await this.makeRequest(`/chats/${sessionId}/messages`);
    
    // ✅ CORRECCIÓN: Backend devuelve response.messages directamente
    const messages = response.messages || [];
    
    // 🐛 DEBUG LOG TEMPORAL
    console.log('🐛 [DEBUG] Messages del backend:', messages.slice(0, 2));
    console.log('🐛 [DEBUG] Primer mensaje completo:', JSON.stringify(messages[0], null, 2));

    console.log(`✅ [GET-MESSAGES] ${messages.length} mensajes obtenidos`);
    
    // ✅ Retornar mensajes sin transformar (ya están en formato correcto)
    return Array.isArray(messages) ? messages : [];
    
  } catch (error) {
    console.error(`❌ [GET-MESSAGES] Error obteniendo mensajes ${sessionId}:`, error);
    return [];
  }
}

/**
 * 🔧 MÉTODO DE DEBUGGING: Mostrar estado del servicio
 */
getServiceStatus(): any {
  return {
    apiBaseUrl: API_BASE_URL,
    availableMethods: [
      'createChatSession',
      'getUserChatsGrouped', 
      'getChatMessages',
      'searchChats',
      'saveChatMessage',
      'saveMessage',
      'addMessage (compatibility)',
      'chatExists',
      'getChatMessagesDetailed',
      'deleteChatSession'
    ],
    timestamp: new Date().toISOString()
  };
}
}

export const chatHistoryService = new ChatHistoryService();
export default chatHistoryService;
